// src/pages/dashboard/Dashboard.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Colors } from "../../constants/Colors";
import {
  useDashboardData,
  STATUS_COLORS,
  RINCONADA_BOUNDS,
} from "../../hooks/useDashboardData";
import StatCard from "../../components/StatCard";
import BarChart from "../../components/charts/BarChart";
import DonutChart from "../../components/charts/DonutChart";
import "../../styles/Dashboard.css";
import { formatCurrency } from "../../utils/format";

const DEFAULT_CENTER = { lat: 13.5833, lng: 123.3167 };

const RANGE_OPTIONS = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "ytd", label: "YTD" },
];

const RENTAL_STATUS_COLORS = {
  Confirmed: Colors.SECONDARY_BLUE,
  "In Progress": Colors.SECONDARY_BLUE,
  Pending: Colors.WARNING,
  Completed: Colors.SUCCESS,
  Cancelled: Colors.RED,
};

/* ─── Google Maps loader — callback-based (works with loading=async) ─── */
let mapsLoaderPromise = null;

const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (mapsLoaderPromise) return mapsLoaderPromise;

  mapsLoaderPromise = new Promise((resolve, reject) => {
    const cbName = `__gmapsInit_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;

    const timeout = setTimeout(() => {
      delete window[cbName];
      reject(new Error("Google Maps load timed out after 15s"));
    }, 15000);

    window[cbName] = () => {
      clearTimeout(timeout);
      delete window[cbName];

      if (window.google?.maps?.Map) {
        resolve(window.google.maps);
      } else {
        reject(
          new Error(
            "Google Maps callback fired but window.google.maps is undefined — " +
              "check API key restrictions / billing in Google Cloud Console"
          )
        );
      }
    };

    const existing = document.getElementById("google-maps-script");
    if (existing) {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          clearTimeout(timeout);
          delete window[cbName];
          resolve(window.google.maps);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(check);
        clearTimeout(timeout);
        delete window[cbName];
        reject(new Error("Existing Google Maps script never initialized"));
      }, 15000);
      return;
    }

    const el = document.createElement("script");
    el.id = "google-maps-script";
    el.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      `&libraries=places,marker&loading=async&callback=${cbName}`;
    el.async = true;
    el.defer = true;
    el.onerror = () => {
      clearTimeout(timeout);
      delete window[cbName];
      reject(new Error("Failed to load Google Maps script (network error)"));
    };
    document.head.appendChild(el);
  });

  return mapsLoaderPromise;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState("30d");

  const {
    stats,
    activity,
    dist,
    topOperators,
    recentRentals,
    rentals,
    locations,
    loading,
    error,
    setError,
  } = useDashboardData(range);

  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showRentalModal, setShowRentalModal] = useState(false);

  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState(null);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowsRef = useRef([]);

  /* ─── Load Google Maps once per app lifetime ──────────── */
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        if (!cancelled) setMapsError("Google Maps API key missing");
        return;
      }

      try {
        await loadGoogleMaps(apiKey);
        if (!cancelled) setMapsReady(true);
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("Google Maps failed to load:", err);
          setMapsError("Failed to load Google Maps");
        }
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ─── Marker helpers ─────────────────────────────────── */
  const clearMarkers = useCallback(() => {
    infoWindowsRef.current.forEach((iw) => {
      try {
        iw.close();
      } catch {
        /* ignore */
      }
    });
    infoWindowsRef.current = [];

    markersRef.current.forEach((m) => {
      try {
        if (window.google?.maps?.event) {
          window.google.maps.event.clearInstanceListeners(m);
        }
        m.setMap(null);
      } catch {
        /* ignore */
      }
    });
    markersRef.current = [];
  }, []);

  /* ─── Map init / update ──────────────────────────────── */
  useEffect(() => {
    if (!mapsReady || !window.google?.maps?.Map || !mapRef.current) return;

    try {
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: DEFAULT_CENTER,
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: "cooperative",
          restriction: {
            latLngBounds: {
              north: RINCONADA_BOUNDS.maxLat,
              south: RINCONADA_BOUNDS.minLat,
              east: RINCONADA_BOUNDS.maxLng,
              west: RINCONADA_BOUNDS.minLng,
            },
            strictBounds: false,
          },
          styles: [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
            { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
          ],
        });
      }

      const map = mapInstanceRef.current;
      clearMarkers();

      if (!locations.length) {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(11);
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();

      locations.forEach((op) => {
        const position = { lat: op.latitude, lng: op.longitude };
        const color = STATUS_COLORS[op.status] || STATUS_COLORS.default;

        const marker = new window.google.maps.Marker({
          position,
          map,
          title: `${op.name} – ${op.status}`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: op.isOutsideBounds ? 9 : 11,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 2.5,
          },
          zIndex: op.isOutsideBounds ? 1 : 2,
        });

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding:12px;min-width:200px;font-family:system-ui,sans-serif;">
              <h4 style="margin:0 0 8px;font-size:15px;font-weight:600;">${escapeHtml(op.name)}</h4>
              <div style="display:flex;align-items:center;margin-bottom:8px;">
                <span style="width:10px;height:10px;border-radius:50%;background:${color};margin-right:8px;"></span>
                <span style="font-weight:500;color:#555;">${escapeHtml(op.status)}</span>
              </div>
              <p style="margin:4px 0;color:#666;font-size:13px;"><strong>Email:</strong> ${escapeHtml(op.email)}</p>
              <p style="margin:4px 0;color:#666;font-size:13px;"><strong>Phone:</strong> ${escapeHtml(op.phone)}</p>
              ${op.isStale ? `<div style="margin-top:10px;padding:6px 8px;background:#fff3cd;border-radius:6px;color:#856404;font-size:12px;">⚠️ Location may be stale</div>` : ""}
              ${op.isOutsideBounds ? `<div style="margin-top:6px;padding:6px 8px;background:#f8d7da;border-radius:6px;color:#721c24;font-size:12px;">📍 Outside Rinconada bounds</div>` : ""}
            </div>`,
        });
        infoWindowsRef.current.push(infoWindow);

        marker.addListener("click", () => {
          infoWindowsRef.current.forEach((iw) => iw.close());
          infoWindow.open({ anchor: marker, map });
        });

        markersRef.current.push(marker);
        bounds.extend(position);
      });

      if (markersRef.current.length) {
        map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
        window.google.maps.event.addListenerOnce(map, "idle", () => {
          if (map.getZoom() > 14) map.setZoom(14);
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Map init/update error:", err);
    }
  }, [mapsReady, locations, clearMarkers]);

  /* ─── Cleanup on unmount ─────────────────────────────── */
  useEffect(() => {
    return () => {
      clearMarkers();
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
    };
  }, [clearMarkers]);

  /* ─── Fleet status summary (from last known locations) ── */
  const statusCounts = useMemo(() => {
    const counts = {};
    locations.forEach((op) => {
      counts[op.status] = (counts[op.status] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [locations]);

  /* ─── Calendar helpers ───────────────────────────────── */
  const rentalsByDay = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const map = new Map();
    rentals.forEach((r) => {
      const t = new Date(r.created_at || r.pickup_date || r.createdAt);
      if (Number.isNaN(t.getTime())) return;
      if (t.getMonth() !== m || t.getFullYear() !== y) return;
      const d = t.getDate();
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(r);
    });
    return map;
  }, [rentals, currentDate]);

  const getRentalsForDay = useCallback(
    (d) => rentalsByDay.get(d) || [],
    [rentalsByDay]
  );

  const handleDateClick = useCallback(
    (day) => {
      const list = getRentalsForDay(day);
      if (!list.length) return;
      setSelectedDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
      );
      setShowRentalModal(true);
    },
    [currentDate, getRentalsForDay]
  );

  const closeRentalModal = useCallback(() => {
    setShowRentalModal(false);
  }, []);

  /* ─── Modal: Escape key + scroll lock ────────────────── */
  useEffect(() => {
    if (!showRentalModal) return;

    const onKey = (e) => {
      if (e.key === "Escape") closeRentalModal();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [showRentalModal, closeRentalModal]);

  const navigateMonth = (dir) =>
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(prev.getMonth() + dir);
      return d;
    });

  const getRentalStatusColor = (s) => RENTAL_STATUS_COLORS[s] || Colors.GRAY;
  const getDaysInMonth = (d) =>
    new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (d) =>
    new Date(d.getFullYear(), d.getMonth(), 1).getDay();
  const formatMonth = (d) =>
    d.toLocaleString("default", { month: "long", year: "numeric" });
  const formatDate = (d) =>
    d.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const formatRelative = (date) => {
    if (!date) return "";
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      }),
    []
  );

  /* ─── Calendar renderer ──────────────────────────────── */
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const cells = [];
    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const today = new Date();

    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`e-${i}`} className="calendar-day empty" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isToday =
        day === today.getDate() &&
        currentDate.getMonth() === today.getMonth() &&
        currentDate.getFullYear() === today.getFullYear();

      const dayRentals = getRentalsForDay(day);
      const hasRentals = dayRentals.length > 0;

      cells.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? "today" : ""} ${
            hasRentals ? "has-rentals" : ""
          }`}
          onClick={hasRentals ? () => handleDateClick(day) : undefined}
          role={hasRentals ? "button" : undefined}
          tabIndex={hasRentals ? 0 : undefined}
          aria-label={hasRentals ? `${day} - ${dayRentals.length} rentals` : undefined}
          onKeyDown={(e) => {
            if (hasRentals && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              handleDateClick(day);
            }
          }}
        >
          <span className="day-num">{day}</span>
          {hasRentals && (
            <div className="rental-indicators">
              {dayRentals.slice(0, 3).map((r, i) => (
                <div
                  key={i}
                  className="rental-indicator"
                  style={{ backgroundColor: getRentalStatusColor(r.status) }}
                />
              ))}
              {dayRentals.length > 3 && (
                <span className="more-indicators">+{dayRentals.length - 3}</span>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="calendar-grid">
        {weekDays.map((d) => (
          <div key={d} className="calendar-header">
            {d.slice(0, 2)}
          </div>
        ))}
        {cells}
      </div>
    );
  };

  /* ─── Loading skeleton ───────────────────────────────── */
  if (loading && stats.length === 0) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-skeleton">
          <div className="skel-header" />
          <div className="skel-grid">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skel-card" />
            ))}
          </div>
          <div className="skel-charts">
            <div className="skel-chart-lg" />
            <div className="skel-chart-sm" />
          </div>
        </div>
      </div>
    );
  }

  const modalRentals = selectedDate
    ? getRentalsForDay(selectedDate.getDate())
    : [];

  /* ─── Render ─────────────────────────────────────────── */
  return (
    <div className="dashboard-container">
      {error && (
        <div
          className="alert alert-warning alert-dismissible fade show d-flex align-items-center"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2" />
          <div className="flex-grow-1">{error}</div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setError(null)}
          />
        </div>
      )}

      {/* ============ HEADER ============ */}
      <header className="dashboard-header">
        <div className="dashboard-header-inner">
          <div>
            <p className="dashboard-eyebrow">{todayLabel}</p>
            <h1 className="dashboard-title">{greeting()}, admin</h1>
            <p className="dashboard-subtitle">
              Here's how Arkila is doing over the selected period.
            </p>
          </div>
          <div className="range-toggle" role="tablist" aria-label="Time range">
            {RANGE_OPTIONS.map((o) => (
              <button
                key={o.id}
                role="tab"
                aria-selected={range === o.id}
                className={`range-btn ${range === o.id ? "active" : ""}`}
                onClick={() => setRange(o.id)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ============ MAIN + SIDE RAIL ============ */}
      <div className="db-layout">
        {/* ---------- MAIN COLUMN ---------- */}
        <div className="db-main">
          <section className="db-stats" aria-label="Key metrics">
            {stats.map((s, i) => (
              <div key={s.key} className="reveal" style={{ "--i": i }}>
                <StatCard stat={s} />
              </div>
            ))}
          </section>

          {/* Activity */}
          <section className="dashboard-card reveal" style={{ "--i": 4 }}>
            <div className="card-head">
              <div>
                <h2 className="card-section-title">Jeepney activity</h2>
                <p className="card-caption">Rentals by status, last 6 months</p>
              </div>
              <div className="chart-legend">
                {[
                  { label: "Active", color: Colors.SECONDARY_BLUE },
                  { label: "Completed", color: Colors.SUCCESS },
                  { label: "Pending", color: Colors.WARNING },
                  { label: "Cancelled", color: Colors.RED },
                ].map((l) => (
                  <div key={l.label} className="legend-item">
                    <span
                      className="legend-dot"
                      style={{ backgroundColor: l.color }}
                    />
                    <span>{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-pad">
              <div className="chart-area">
                {activity.every((d) => d.total === 0) ? (
                  <div className="chart-empty">
                    <i className="bi bi-bar-chart fs-2 text-muted" />
                    <p className="mb-0 mt-2 text-muted small">
                      No activity data for the last 6 months
                    </p>
                  </div>
                ) : (
                  <BarChart data={activity} />
                )}
              </div>
            </div>
          </section>

          {/* Map */}
          <section className="dashboard-card reveal" style={{ "--i": 5 }}>
            <div className="card-head">
              <div>
                <h2 className="card-section-title">
                  Operator locations
                  <span className="count-pill">{locations.length}</span>
                </h2>
                <p className="card-caption">Last known positions, not live</p>
              </div>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => navigate("/location")}
              >
                View full tracking
              </button>
            </div>

            {statusCounts.length > 0 && (
              <div className="status-chips">
                {statusCounts.map(([status, count]) => {
                  const c = STATUS_COLORS[status] || STATUS_COLORS.default;
                  return (
                    <div key={status} className="status-chip">
                      <span className="status-chip-dot" style={{ backgroundColor: c }} />
                      <span className="status-chip-count">{count}</span>
                      <span className="status-chip-label">{status}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="map-container-wrapper">
              {!mapsReady && !mapsError && (
                <div className="map-placeholder">
                  <div className="spinner-border spinner-border-sm text-primary" />
                  <span className="text-muted small">Loading map…</span>
                </div>
              )}
              {mapsError && (
                <div className="map-placeholder">
                  <i className="bi bi-exclamation-triangle fs-3 text-warning" />
                  <p className="mb-0 text-muted small">{mapsError}</p>
                </div>
              )}
              {mapsReady && locations.length === 0 && (
                <div className="map-placeholder">
                  <i className="bi bi-geo-alt fs-3 text-muted" />
                  <p className="mb-0 text-muted small">
                    No operator locations available
                  </p>
                </div>
              )}
              <div ref={mapRef} className="map-container" />

            </div>
          </section>

          {/* Recent rentals table */}
          <section className="dashboard-card reveal" style={{ "--i": 6 }}>
            <div className="card-head">
              <div>
                <h2 className="card-section-title">Recent rentals</h2>
                <p className="card-caption">Latest bookings across all operators</p>
              </div>
            </div>

            {recentRentals.length === 0 ? (
              <div className="chart-empty card-pad">
                <i className="bi bi-inbox fs-2 text-muted" />
                <p className="mb-0 mt-2 text-muted small">No recent rentals</p>
              </div>
            ) : (
              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th>Passenger</th>
                      <th className="hide-sm">Purpose</th>
                      <th className="hide-sm">When</th>
                      <th>Status</th>
                      <th className="num">Fare</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRentals.map((r) => {
                      const statusColor = getRentalStatusColor(r.status);
                      const name =
                        `${r.passenger?.first_name || ""} ${r.passenger?.last_name || ""}`.trim() ||
                        r.passenger_name ||
                        "Unknown";
                      return (
                        <tr key={r.rental_id || r.id}>
                          <td>
                            <div className="cell-person">
                              <span className="avatar">
                                {name.charAt(0).toUpperCase()}
                              </span>
                              <span className="cell-name">{name}</span>
                            </div>
                          </td>
                          <td className="hide-sm muted">{r.purpose || "Rental"}</td>
                          <td className="hide-sm muted">{formatRelative(r._date)}</td>
                          <td>
                            <span
                              className="status-pill"
                              style={{
                                color: statusColor,
                                backgroundColor: `${statusColor}1f`,
                              }}
                            >
                              <span
                                className="status-pill-dot"
                                style={{ backgroundColor: statusColor }}
                              />
                              {r.status}
                            </span>
                          </td>
                          <td className="num strong">{formatCurrency(r.fare)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* ---------- SIDE RAIL ---------- */}
        <aside className="db-rail">
          {/* Calendar */}
          <section className="dashboard-card reveal" style={{ "--i": 7 }}>
            <div className="card-head">
              <h2 className="card-section-title">Rental calendar</h2>
              <div className="calendar-nav-inline">
                <button
                  className="calendar-nav-btn"
                  onClick={() => navigateMonth(-1)}
                  aria-label="Previous month"
                >
                  <i className="bi bi-chevron-left" />
                </button>
                <button
                  className="calendar-nav-btn"
                  onClick={() => navigateMonth(1)}
                  aria-label="Next month"
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            </div>
            <div className="card-pad pt-0">
              <p className="calendar-month">{formatMonth(currentDate)}</p>
              {renderCalendar()}
              <p className="calendar-hint">Tap a day with dots to view rentals</p>
            </div>
          </section>

          {/* Distribution */}
          <section className="dashboard-card reveal" style={{ "--i": 8 }}>
            <div className="card-head">
              <h2 className="card-section-title">Status split</h2>
            </div>
            <div className="card-pad pt-0">
              {dist.total === 0 ? (
                <div className="chart-empty py-4">
                  <i className="bi bi-pie-chart fs-2 text-muted" />
                  <p className="mb-0 mt-2 text-muted small">No rentals yet</p>
                </div>
              ) : (
                <>
                  <DonutChart dist={dist} />
                  <div className="pie-legend">
                    {[
                      { label: "Active", value: dist.confirmed, color: Colors.SECONDARY_BLUE },
                      { label: "Completed", value: dist.completed, color: Colors.SUCCESS },
                      { label: "Pending", value: dist.pending, color: Colors.WARNING },
                      dist.cancelled > 0 && {
                        label: "Cancelled",
                        value: dist.cancelled,
                        color: Colors.RED,
                      },
                    ]
                      .filter(Boolean)
                      .map((l) => (
                        <div key={l.label} className="pie-legend-row">
                          <div className="d-flex align-items-center">
                            <span
                              className="legend-dot"
                              style={{ backgroundColor: l.color }}
                            />
                            <span>{l.label}</span>
                          </div>
                          <strong>
                            {l.value} ({Math.round((l.value / dist.total) * 100)}%)
                          </strong>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Top operators */}
          <section className="dashboard-card reveal" style={{ "--i": 9 }}>
            <div className="card-head">
              <h2 className="card-section-title">Top operators</h2>
            </div>
            <div className="card-pad pt-0">
              {topOperators.length === 0 ? (
                <div className="chart-empty py-4">
                  <i className="bi bi-trophy fs-2 text-muted" />
                  <p className="mb-0 mt-2 text-muted small">
                    No completed trips in this period
                  </p>
                </div>
              ) : (
                <div className="leader-list">
                  {topOperators.map((op, i) => (
                    <div key={op.id} className="leader-row">
                      <span className={`leader-rank rank-${i + 1}`}>{i + 1}</span>
                      <div className="overflow-hidden">
                        <div className="leader-name">{op.name}</div>
                        <div className="leader-meta">
                          {formatCurrency(op.revenue)} earned
                        </div>
                      </div>
                      <span className="leader-trips">
                        {op.trips} trip{op.trips !== 1 ? "s" : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </aside>
      </div>

      {/* ============ RENTAL MODAL ============ */}
      {showRentalModal && selectedDate && (
        <>
          <div className="modal-backdrop fade show" onClick={closeRentalModal} />
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rental-modal-title"
          >
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title fw-bold" id="rental-modal-title">
                    Rentals on {formatDate(selectedDate)}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeRentalModal}
                  />
                </div>
                <div className="modal-body">
                  {modalRentals.length > 0 ? (
                    modalRentals.map((r) => (
                      <div key={r.rental_id || r.id} className="rental-item">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="mb-0 fw-semibold">
                              {r.passenger?.first_name || r.passenger_name || "Unknown"}{" "}
                              {r.passenger?.last_name || ""}
                            </h6>
                            <small className="text-muted">
                              Rental #{r.rental_id || r.id}
                            </small>
                          </div>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: getRentalStatusColor(r.status),
                              color: "#fff",
                            }}
                          >
                            {r.status}
                          </span>
                        </div>
                        <div className="row g-2">
                          <div className="col-md-6">
                            <p className="mb-1 small">
                              <i className="bi bi-truck me-2 text-muted" />
                              {r.jeepney?.model || "N/A"} (
                              {r.jeepney?.license_plate || "N/A"})
                            </p>
                            <p className="mb-1 small">
                              <i className="bi bi-geo-alt me-2 text-muted" />
                              From: {r.pickup_location || "N/A"}
                            </p>
                            <p className="mb-0 small">
                              <i className="bi bi-person me-2 text-muted" />
                              Operator: {r.operator?.first_name || "Unknown"}{" "}
                              {r.operator?.last_name || ""}
                            </p>
                          </div>
                          <div className="col-md-6">
                            <p className="mb-1 small">
                              <i className="bi bi-geo me-2 text-muted" />
                              To: {r.destination || "N/A"}
                            </p>
                            <p className="mb-1 small">
                              <i className="bi bi-cash me-2 text-muted" />
                              Amount: {formatCurrency(r.fare)}
                            </p>
                            <p className="mb-0 small">
                              <i className="bi bi-tag me-2 text-muted" />
                              Purpose: {r.purpose || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5">
                      <i className="bi bi-calendar-x fs-1 text-muted" />
                      <p className="mt-2 text-muted">No rentals for this date</p>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeRentalModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Utility: HTML escape (for InfoWindow content) ─── */
function escapeHtml(str) {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export default Dashboard;